import React, {ReactNode, useEffect, useState, useCallback} from 'react';
import {createGlobalStyle, css} from 'styled-components';
import {Previewer} from 'pagedjs';
import {isDesktop, isIOS} from 'react-device-detect';
import {NotificationProvider} from 'contexts';

const PDFPreview = ({
    showAsPDF,
    isError,
    children,
    readyHandler,
    pageTitle,
    pdfHeaderElement,
    layout,
}: {
    showAsPDF: boolean;
    isError: boolean;
    children: ReactNode;
    readyHandler?: () => Promise<void>;
    pageTitle: string;
    pdfHeaderElement: JSX.Element;
    layout?: 'portrait' | 'landscape';
}) => {
    const [pageRendered, setPageRendered] = useState(false);
    const getPdfCss = useCallback((layout = 'portrait') => {
        const pdfCss = css`
            @page {
                size: A4 ${layout};
                margin: 13mm 13mm 13mm 13mm;
                @bottom-right {
                    content: 'PAGE ' counter(page) ' of ' counter(pages);
                    color: #918f8f;
                    font-size: 14px;
                    font-weight: bold;
                }
                @bottom-left {
                    content: var(--pdf-generated-date);
                    color: #918f8f;
                    font-size: 11px;
                }
            }

            .landscape {
                page: landscape;
            }

            .link::after {
                content: target-counter(attr(data-href string), page);
            }

            .new-page {
                page-break-before: always;
            }

            section,
            img {
                break-inside: avoid;
            }
        `.join();
        return URL.createObjectURL(new Blob([pdfCss], {type: 'text/css'}));
    }, []);

    useEffect(() => {
        if (showAsPDF) {
            const paged = new Previewer();
            const DOMContent = document.getElementById('root');
            void paged
                .preview(DOMContent, [getPdfCss(layout)], document.body)
                .then(() => {
                    document.title = pageTitle;
                    // once preview is ready, let's remove react node to display only the preview version
                    const rootElement = document.getElementById('root');
                    if (rootElement) {
                        rootElement.remove();
                    }
                    if (readyHandler) {
                        void readyHandler();
                    } else {
                        // dispatch pdfReady, so puppeteer know pdf is ready
                        document.dispatchEvent(new Event('pdfReady'));

                        // create header div -- this is where portal be inserted
                        const portalContainer = document.createElement('div');
                        portalContainer.className = 'header-container';
                        document.body.insertBefore(
                            portalContainer,
                            document.body.firstChild
                        );

                        if (!isDesktop) {
                            const page: HTMLDivElement =
                                document.querySelector('.pagedjs_page');
                            const pageWidth = page.offsetWidth; // likely 794
                            const viewportWidth = window.visualViewport
                                ? window.visualViewport.width
                                : window.innerWidth;
                            const scale = viewportWidth / pageWidth;

                            const pagesContainer: HTMLDivElement =
                                document.querySelector('.pagedjs_pages');

                            if (scale < 1) {
                                pagesContainer.style.transform = `scale(${scale})`;
                                pagesContainer.style.transformOrigin =
                                    'top left';
                            }

                            // workaround for interim on issue CO-3683
                            // needs more investigation why on IOS devices page breaks are not consistent
                            if (isIOS) {
                                const pageContents: NodeListOf<HTMLDivElement> =
                                    document.querySelectorAll(
                                        '.pagedjs_pagebox > .pagedjs_area > .pagedjs_page_content'
                                    );
                                pageContents.forEach((pageContent) => {
                                    pageContent.style.height = 'fit-content';
                                });
                            }
                        }
                    }
                    setPageRendered(true);
                });
        }
    }, [showAsPDF, pageTitle]);

    if (isError) {
        // in case of error dispatch pdfReady, so puppeteer stop waiting for this event
        document.dispatchEvent(new Event('pdfReady'));
        return (
            <>
                Something went wrong loading the PDF document. Please try again
                later.
            </>
        );
    }

    return (
        <>
            <GlobalStyle />
            {children}
            {pageRendered ? (
                <NotificationProvider>{pdfHeaderElement}</NotificationProvider>
            ) : null}
        </>
    );
};

const GlobalStyle = createGlobalStyle`
    .flex {
        display: flex;
    }
    .flex-col {
        display: flex;
        flex-direction: column;
    }
    .flex-1 {
        flex: 1;
    }
    .flex-baseline {
        align-items: baseline;
    }
    .hr {
        height: 1px;
        border-bottom: 1px solid #231f20;
    }
    .grey {
        color: #918f8f;
    }
    .header-1 {
        font-size: 28px;
        font-weight: 900;
    }
    .header-2 {
        font-size: 24px;
        font-weight: 900;
    }
    .header-3 {
        font-size: 18px;
        font-weight: 700;
    }
    .mt-5 {
        margin-top: 5px !important;
    }
    .mt-10 {
        margin-top: 10px !important;
    }
    .mt-15 {
        margin-top: 15px !important;
    }
    .mt-40 {
        margin-top: 40px !important;
    }
    .font-xxs {
        font-size: 8px !important;
    }
    .font-xs {
        font-size: 12px !important;
    }
    .font-xs-2 {
        font-size: 13px !important;
    }
    .font-sm {
        font-size: 14px !important;
    }
    .font-lg {
        font-size: 18px !important;
    }
    section {
        break-inside: avoid;
    }
    .pagedjs_pages * {
        font-family: Poppins;
    }
    .pagedjs_page[data-page-number]:not([data-page-number="1"]) div[data-id="job_details"] > :not(div):first-child .optional-margin {
        margin-top: 0 !important;
    }
    .pagedjs_landscape_page .pagedjs_margin-bottom-right > .pagedjs_margin-content{
        position: absolute;
        bottom: 13mm;
        right: calc(210mm - 3.25mm);
        transform: rotate(90deg);
        transform-origin: bottom right;
    }
    .pagedjs_landscape_page .pagedjs_margin-bottom-left > .pagedjs_margin-content {
        position: absolute;
        bottom: 73mm;
        right: calc(210mm - 3.25mm);
        transform: rotate(90deg);
        transform-origin: bottom right;
    }
    .landscape {
        transform: translateX(0) translateY(-100%) rotate(90deg);
        transform-origin: bottom left;
        width: calc(297mm - 26mm);
        height: calc(210mm - 26mm);
    }
    /* not html: pagedjs clones it into every page, and scaled pages show it at the edges */
    .pagedjs_pages, :root {
        background: #282828;
    }
    .pagedjs_page {
        background: #FFFFFF;
        margin: 0 auto 10px;
    }

    /* Prevent flicker after rendering preview */
    .pagedjs_pages .invisible {
        visibility: visible !important;
    }

    .header-container {
        padding-bottom: 59px;
        background: #282828;
    }

    /* anchor jumps land below the fixed .pdf-header */
    html {
        scroll-padding-top: 56px;
    }

    .pagedjs_margin-bottom-left.hasContent {
        visibility: hidden;
    }

    .pagedjs_page:not(.pagedjs_landscape_page) .pagedjs_margin-bottom-right {
        position: relative;
        z-index: 1;
    }

    body {
        background-color: unset;
    }

    .pagedjs_pages body {
        background-color: #FFF;
    }

    @media print {
        /* Prevent header from printing */
        .header-container {
            display: none !important;
        }
        /* Reset to white when printing */
        .pagedjs_pages, :root {
            background: #FFFFFF;
        }
        /* Reset zoom when printing */
        body {
            zoom: 1 !important;
        }
        .pagedjs_pages {
            transform: scale(1) !important;
        }
        .pagedjs_margin-bottom-left.hasContent {
            visibility: visible;
        }
    }
`;

export default PDFPreview;
